import React, { useCallback, useEffect } from 'react';
import t from 'react-translate';
import { css } from '@emotion/core';
import { FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSelector } from 'react-redux';
import { find, omit } from 'underscore';
import { Button } from 'react-bootstrap';

import { RootState } from 'redux/schemas';
import { useAppDispatch } from 'redux/store';
import { openConfirmationDialog } from 'redux/actions/confirmation-dialogs';
import { ActivityType } from 'redux/schemas/models/activity';
import { LectureComponent } from 'redux/schemas/models/lecture-component';
import { MinimalVideoPracticeActivity } from 'redux/schemas/models/video-practice';
import { PracticeActivitiesAvailable } from 'redux/schemas/api/video-practice';
import { CourseAliases } from 'redux/schemas/models/course';
import { NLecturePage } from 'redux/schemas/models/lecture-page';

import { getPracticeActivitiesAvailable } from 'redux/selectors/video-practice-feedback';
import { getCourseAliases } from 'redux/selectors/course';

import NvFormDropdown from 'shared/components/inputs/nv-form-dropdown';
import { NvDropdownTextItem } from 'shared/components/inputs/nv-dropdown';
import NvTextInput from 'shared/components/inputs/nv-text-input';
import { gray3, gray4 } from 'styles/global_defaults/colors';
import { config } from '../../../../config/pendo.config.json';

const styles = css`
  .nv-text-input {
    max-width: 60px;
  }
  .nv-form-dropdown .bs4-dropdown .bs4-dropdown-menu {
    max-height: 150px;
    overflow-y: auto;
  }
  .bs4-dropdown-menu {
    width: 250px;
    overflow-x: hidden;
    .bs4-dropdown-item {
      white-space: normal;
      word-break: break-word;
    }
  }
  .form-title {
    max-width: 250px;
  }
  .disabled {
    background-color: #fff !important;
    pointer-events: none;
    .bs4-dropdown {
      border-color: ${gray4};
      .icon-dropdown-arrow {
        color: ${gray3};
      }
    }
  }
`;

type FormData = {
  videoPractice: NvDropdownTextItem,
  count: number,
};
type FeedbackCriteria = {
  activityId: number,
  activityType: ActivityType.VIDEO_PRACTICE,
  requiredToComplete: number,
  title: string,
  description: string,
  hardDeadline: boolean,
  isTodo: boolean,
  activity?: MinimalVideoPracticeActivity
};

type VideoPracticeEvaluationProps = {
  onClose?: () => void,
  createVideoPracticeEvaluation: (data: any) => void
  editBasics: (data: FeedbackCriteria) => void
  isEdit?: boolean
  formData?: FeedbackCriteria
  setModalTitle?: (title: string) => void
  lectureComponent?: LectureComponent
  onChange: (isDirty: boolean) => void
};

const VideoPracticeEvaluation = ({
  onClose,
  createVideoPracticeEvaluation,
  editBasics,
  isEdit,
  formData,
  setModalTitle,
  lectureComponent,
  onChange,
}: VideoPracticeEvaluationProps) => {
  const practiceActivitiesAvailable = useSelector<RootState, PracticeActivitiesAvailable>(state => getPracticeActivitiesAvailable(state));
  const aliases = useSelector<RootState, CourseAliases>(state => getCourseAliases(state));
  const lecturePage: NLecturePage = useSelector(state => state.models.lecturePages[lectureComponent?.lecturePageId]);

  const dispatch = useAppDispatch();

  const dropdownItems = useCallback(() => {
    const activities: NvDropdownTextItem[] = practiceActivitiesAvailable?.map(activity => ({
      type: 'text',
      id: activity.id,
      value: activity.id,
      text: activity.title,
      dataQa: `select-practice-activity-${activity.id}`,
    }));
    if (isEdit && formData.activity) {
      activities.push({
        type: 'text',
        id: formData.activity.id,
        value: formData.activity.id,
        text: formData.activity.title,
        dataQa: `select-practice-activity-${formData.activity.id}`,
      });
    }
    return activities;
  }, [practiceActivitiesAvailable, formData?.activity, isEdit]);

  const validationSchema = yup.object({
    videoPractice: yup.object().shape({
      value: yup.number().required(t.VALIDATION.REQUIRED()),
    }).required(),
    count: yup.number().min(1, t.VALIDATION.MIN('1')).required(t.VALIDATION.REQUIRED()),
  });

  const methods = useForm<FormData>({
    mode: 'onChange',
    defaultValues: {
      videoPractice: formData && formData?.activity
        ? find(dropdownItems(),
          (item: NvDropdownTextItem) => (
            item.id === formData.activityId
          )) : null,
      count: formData?.requiredToComplete ?? 5,
    },
    resolver: yupResolver(validationSchema) as any,
  });
  const { handleSubmit, formState } = methods;
  const { isDirty, isValid } = formState;

  useEffect(() => {
    const title = isEdit
      ? t.LECTURE_PAGES.COMPONENTS.DROPDOWN.EDIT_BASICS()
      : t.LECTURE_PAGES.COMPONENTS.PEER_EVALUATION.VIDEO_PRACTICE.MODAL.TITLE();
    setModalTitle?.(title);
  }, [isEdit, setModalTitle]);

  const onSubmit = (data: FormData) => {
    if (isEdit) {
      editBasics({
        ...omit(formData, 'activity'),
        requiredToComplete: data.count,
        activityId: data.videoPractice.id as number,
        activityType: ActivityType.VIDEO_PRACTICE,
      });
    } else {
      const feedbackCriteria: FeedbackCriteria = {
        activityId: data.videoPractice.id as number,
        activityType: ActivityType.VIDEO_PRACTICE,
        requiredToComplete: data.count,
        title: t.LECTURE_PAGES.COMPONENTS.PEER_EVALUATION.VIDEO_PRACTICE.FEEDBACK_TITLE(data.videoPractice.text),
        description: `<div class="froala-style-regular">${t.LECTURE_PAGES.COMPONENTS.PEER_EVALUATION.VIDEO_PRACTICE.FEEDBACK_DESCRIPTION({
          ...aliases.learnersAliases, ...aliases.courseAliases,
        })}</div>`,
        hardDeadline: false,
        isTodo: false,
      };
      createVideoPracticeEvaluation(feedbackCriteria);
    }
  };

  const onCancel = () => {
    if (isDirty) {
      dispatch(openConfirmationDialog({
        onConfirm: onClose,
        cancelText: t.FORM.CANCEL(),
        confirmText: t.FORM.DISCARD_CHANGES(),
        title: t.FORM.UNSAVED_CHANGES.NAVIGATE_AWAY_CHANGES(),
      }));
    } else {
      onClose();
    }
  };

  useEffect(() => {
    onChange(isDirty);
  }, [isDirty, onChange]);

  const isActivityDisabled = lecturePage?.released && !!formData?.activityId;
  const isDropdownDisabled = isEdit && !formData?.activity && !practiceActivitiesAvailable?.length;

  return (
    <div className='p-4 d-flex flex-column' css={styles}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className='gray-3 text-small font-weight-bold mb-1'>
            {t.LECTURE_PAGES.COMPONENTS.PEER_EVALUATION.VIDEO_PRACTICE.MODAL.PICK_VIDEO_PRACTICE()}
          </div>
          <NvFormDropdown
            items={dropdownItems()}
            name='videoPractice'
            isSmallSize
            className={`d-flex my-2 ${isActivityDisabled || isDropdownDisabled ? 'disabled' : ''}`}
            title={t.LECTURE_PAGES.COMPONENTS.PEER_EVALUATION.VIDEO_PRACTICE.MODAL.SELECT_VIDEO_PRACTICE()}
            titleClass={`font-weight-bolder form-title ${isActivityDisabled || isDropdownDisabled ? 'gray-5' : 'gray-2'}`}
          />
          {isActivityDisabled && (
            <div className='text-small gray-3'>
              {t.LECTURE_PAGES.COMPONENTS.PEER_EVALUATION.VIDEO_PRACTICE.MODAL.ACTIVITY_DISABLED({ ...aliases.lectureAliases })}
            </div>
          )}
          <div className='text-small gray-1 mb-2 mt-4'>
            {t.LECTURE_PAGES.COMPONENTS.PEER_EVALUATION.VIDEO_PRACTICE.MODAL.COUNT_DESCRIPTION()}
          </div>
          <NvTextInput
            withForm
            name='count'
            className='d-flex'
          />
          <div className='d-flex mt-6 justify-content-center'>
            <Button
              variant='secondary mr-2'
              onClick={onCancel}
              data-qa={config.pendo.practiceFeedback.cancel}
            >
              {t.FORM.CANCEL()}
            </Button>
            <Button
              variant='primary'
              type='submit'
              disabled={!isValid || !isDirty}
              data-qa={isEdit ? config.pendo.practiceFeedback.update : config.pendo.practiceFeedback.create}
            >
              {isEdit ? t.FORM.UPDATE() : t.FORM.ADD()}
            </Button>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

export default VideoPracticeEvaluation;
